import { debug } from "debug";

import { LOG_REGISTRY, type LogRegistry, type LogRegistryLeafNode } from "./registry";

const DEBUG_LOG_LEVEL_KEY = "debugLogLevel";
const LIBRARY_NAMESPACE = "novo";

const LOG_LEVEL = Object.freeze({
    debug: "debug",
    info: "info",
    warn: "warn",
    error: "error",
});
type LogLevel = keyof typeof LOG_LEVEL;

const LOG_LEVEL_PRECEDENCE: { [key in LogLevel]: number } = Object.freeze({
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
});

// Determine CURRENT_LOG_LEVEL from local storage
function isLogLevel(level: string): level is LogLevel {
    return level in LOG_LEVEL_PRECEDENCE;
}

function getLogLevelFromLocalStorage(): LogLevel {
    const lsLevel = localStorage.getItem(DEBUG_LOG_LEVEL_KEY);
    if (lsLevel === null) {
        return LOG_LEVEL.debug;
    }
    if (isLogLevel(lsLevel)) {
        return lsLevel;
    } else {
        console.warn(`Invalid log level in localStorage.${DEBUG_LOG_LEVEL_KEY}: '${lsLevel}'.

  Valid levels are: [${Object.keys(LOG_LEVEL)
      .map((level) => `'${level}'`)
      .join(", ")}].

  Falling back to '${LOG_LEVEL.debug}'.`);
        return LOG_LEVEL.debug;
    }
}

const CURRENT_LOG_LEVEL = getLogLevelFromLocalStorage();

// Single noop reference to point to when logging is disabled
function noop(): void {}

function logWithLevel(level: LogLevel, namespace: string) {
    if (LOG_LEVEL_PRECEDENCE[level] < LOG_LEVEL_PRECEDENCE[CURRENT_LOG_LEVEL]) {
        return noop;
    }
    return debug(namespace);
}

type Log = {
    [key in LogLevel]: (...args: unknown[]) => void;
};
export const debugFactory = (namespace: string): Log => {
    return {
        debug: logWithLevel("debug", namespace),
        info: logWithLevel("info", namespace),
        warn: logWithLevel("warn", namespace),
        error: logWithLevel("error", namespace),
    };
};

export const isDebugEnabled = (namespace: string): boolean => {
    return debug.enabled(namespace);
};

type LogMetaData = { namespace: string };
type LogNamespaceTree<T> = {
    [K in keyof T]: T[K] extends LogRegistryLeafNode
        ? Log & LogMetaData // Leaf node
        : LogNamespaceTree<T[K]>; // Nested node
} & LogMetaData;
function buildLog<T extends LogRegistry>(logRegistry: T, namespace = LIBRARY_NAMESPACE): LogNamespaceTree<T> {
    const log: Record<string, unknown> = { namespace };

    for (const key in logRegistry) {
        const childNamespace = `${namespace}:${key}`;
        const entry = logRegistry[key];
        if (Object.keys(entry).length === 0) {
            // Leaf node, create debug functions
            log[key] = debugFactory(childNamespace);
            (log[key] as LogMetaData).namespace = childNamespace;
        } else {
            // Nested node, recurse
            log[key] = buildLog(entry, childNamespace);
        }
    }

    return log as LogNamespaceTree<T>;
}

export const log = buildLog(LOG_REGISTRY);
